import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { useRequest } from 'ahooks';
import numeral from 'numeral';
import { Spin, Row, Col } from 'antd';
import { history, useParams } from 'umi';
import { ArrowRightOutlined } from 'iglooicon';
import classnames from 'classnames';
import useMobile from '@/hooks/useMobile';
import ProductCard from '@/components/product-card';
import AmountWithUnit from '@/components/amount-with-unit';
import useUpdateRemoteLangMessage from '@/hooks/useUpdateRemoteLangMessage';
import FastQuoteIcon from '@/assets/img/fast_quote.svg';
import { GetProductList } from './service';
import * as SESSION_KEYS from '@/configs/session-storage-keys';
import './index.less';

const SHOWPRODUCT = [
  '19313350-f99a-4168-a45b-be9241591a6b',
  '051dd494-7ec6-44b9-bda3-4d512374e5ab',
  '22a17c6e-7ccf-45c6-aa48-19fa8cfa116a',
  'a7cf5fe2-d9e2-4cee-b1df-760b99d3113b',
  '9ba0d025-ae55-4107-8e79-a1e92d221357',
  '6f2ff352-b45b-44d3-abc1-eef1ef293d88',
  '88d5c289-33ab-4b00-a649-c2ee22a99906',
  'f41f03bc-358a-433e-95f9-75e8404a1e68',
];

const useProductList = () => {
  const { formatMessage } = useIntl();
  const params = useParams<{ type: TTypes }>();
  const productType = params.type;

  const isMobile = useMobile();

  const [filterBy, setFilterBy] = useState('');
  const [productGroups, setProductGroups] = useState<{
    [key: string]: any[];
  }>({});

  const hiddenFastQuote = ['travel', 'pa', 'health', 'pd'].includes(
    productType,
  );

  const { loading, run } = useRequest<{ plans: any[] }, any>(GetProductList, {
    manual: true,
    onSuccess: (res) => {
      const plans = res.plans.filter((e) => !!e);

      const insureList: { [key: string]: any } = {}; // productGroups

      plans.forEach((plan) => {
        const { coverage_type } = plan?.addl_data || {};
        if (insureList[coverage_type]) {
          insureList[coverage_type].push(plan);
        } else {
          insureList[coverage_type] = [plan];
        }
      });
      setProductGroups(insureList);
    },
  });

  useUpdateRemoteLangMessage([() => run({ productType })]);

  useEffect(() => {
    run({ productType });

    if (['pd', 'motor'].includes(productType)) {
      setFilterBy(productType === 'pd' ? 'cpd' : 'tpl');
    }

    // event tracking: product landing page
    window.gtag('event', 'product_landing_page', {
      product_name: productType === 'pd' ? 'motor' : productType,
      user_unique_id: JSON.parse(localStorage.getItem('user_info') ?? '{}')?.id,
    });
  }, [productType]);

  const fastQuoteProductNameMaps = {
    motor: formatMessage({
      id: 'Third Party Liability Insurance',
    }),
    pd: formatMessage({
      id: 'Physical Damage Insurance',
    }),
    travel: formatMessage({
      id: 'Travel Insurance',
    }),
    pa: formatMessage({
      id: 'Personal Accident',
    }),
    health: formatMessage({
      id: 'Hospital Cash',
    }),
  };

  const Dom = (
    <div
      className={classnames([
        'productListContainer',
        {
          'is-mobile': isMobile,
        },
      ])}
    >
      {!hiddenFastQuote && (
        <div
          className="fastQuote"
          onClick={() => {
            history.push({
              pathname: `/home/${productType}/quote`,
              state: {
                from: 'product-list',
              },
              query: {
                totalStep: '3',
              },
            });

            // event tracking: fast quote
            window.gtag('event', 'click_fastquote', {
              product_name: productType,
              user_unique_id: JSON.parse(
                localStorage.getItem('user_info') ?? '{}',
              )?.id,
            });
          }}
        >
          <div className="leftPart">
            <img src={FastQuoteIcon} />
            <div>
              {formatMessage(
                {
                  id: 'Get accurate quotes with minimal entry for {product}',
                },
                {
                  product: (
                    <span className="fastQuoteInsureName">
                      {fastQuoteProductNameMaps[productType]}
                    </span>
                  ),
                },
              )}
            </div>
          </div>
          <ArrowRightOutlined />
        </div>
      )}

      <Spin spinning={loading}>
        {Object.keys(productGroups)
          .filter((type) => (filterBy === '' ? true : type === filterBy))
          .map((type) => {
            return (
              <div className="productTypeContainer" key={type}>
                <Row gutter={isMobile ? [0, 12] : [8, 16]}>
                  {productGroups[type]?.map((product) => {
                    if (!SHOWPRODUCT.includes(product.object.product_key))
                      return;
                    const { object, addl_data } = product;
                    const { cms_product_detail } = addl_data;
                    let highlights: { [prop: string]: any } =
                      cms_product_detail?.data?.attributes?.highlights || [];

                    highlights = highlights.filter(
                      (e: any) => e?.text && e?.text?.trim() !== '',
                    );

                    const insurer =
                      cms_product_detail?.data?.attributes?.insurer || {};
                    const logoUrl =
                      insurer?.data?.attributes?.logo?.data?.attributes?.url ||
                      object?.details?.logo_url;
                    const insurerName =
                      insurer?.data?.attributes?.name ||
                      addl_data?.insurer_name;
                    const planName =
                      cms_product_detail?.data?.attributes?.planName ||
                      object?.name;
                    const premiumFrom =
                      cms_product_detail?.data?.attributes?.premiumFrom ?? 0;
                    const commission = numeral(
                      addl_data.commission_rate,
                    ).format('0.[00]%');
                    const price = ['travel', 'pa', 'health'].includes(
                      productType,
                    ) ? (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: 4, fontWeight: 400 }}>
                          {formatMessage({
                            id: 'From',
                          })}
                        </span>
                        <AmountWithUnit
                          amount={Number(premiumFrom)}
                          unit={formatMessage({ id: 'VND' })}
                        />
                      </div>
                    ) : undefined;
                    return (
                      <Col
                        key={object.id}
                        span={isMobile ? 24 : 8}
                        onClick={() => {
                          history.push({
                            pathname: `/home/${
                              productType === 'pd' ? 'motor' : productType
                            }/product-list/${object.product_key}`,
                            query: {
                              planKey: object.plan_key,
                              totalStep: '2',
                              coverageType: addl_data?.coverage_type,
                              soiCategory: addl_data?.soi_category,
                            },
                          });
                        }}
                      >
                        <ProductCard
                          icon={logoUrl}
                          description={highlights}
                          productName={planName}
                          commission={commission}
                          insurerName={insurerName}
                          price={price}
                          productKey={object.product_key}
                          planKey={object.plan_key}
                          buttonOnClick={(button: string) => {
                            history.push({
                              pathname: `/home/${
                                productType === 'pd' ? 'motor' : productType
                              }/quote`,
                              state: {
                                from: 'product-list',
                              },
                              query: {
                                totalStep: '2',
                                button: button,
                              },
                            });
                            sessionStorage.setItem(
                              SESSION_KEYS.PICKED_PLAN_INFO,
                              JSON.stringify({
                                planIcon: object.details.logo_url,
                                planKey: object.plan_key,
                                planName,
                                productKey: object.product_key,
                                coverageType: addl_data?.coverage_type,
                                soiCategory: addl_data?.soi_category,
                              }),
                            );
                          }}
                        />
                      </Col>
                    );
                  })}
                </Row>
              </div>
            );
          })}
      </Spin>
    </div>
  );

  return Dom;
};

export default useProductList;
